import { useWizardProgress } from "@/hooks/useWizardProgress";
import { useWizardStepTimer } from "@/hooks/useWizardStepTimer";
import { RootState } from "@/store";
import {
  decrementCurrentStep,
  incrementCurrentStep,
  setCurrentStep,
} from "@/store/wizard";
import { isEnrolledStatus } from "@/types/employeeState";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ContentWrapper from "../Common/Containers/ContentContainer";
import Beneficiaries from "./Beneficiaries";
import Contribution from "./Contributions";
import Goals from "./Goals";
import Introduction from "./Introduction";
import InvestmentProfile from "./InvestmentProfile";
import SetupComplete from "./SetupComplete";
import Summary from "./Summary";

type WizardProps = {
  modal?: boolean;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  overrideStep?: number;
  isUserEnrolled?: boolean;
  onCancel?: () => void;
};

const WIZARD_STEPS = {
  MODAL: {
    2: Beneficiaries,
    3: Goals,
    4: Contribution,
    5: InvestmentProfile,
    6: Summary,
    7: SetupComplete,
  },

  NON_MODAL: {
    1: Introduction,
    2: Beneficiaries,
    3: Goals,
    4: Contribution,
    5: InvestmentProfile,
    6: Summary,
    7: SetupComplete,
  },
} as const;

const getDefaultComponent = (isModal: boolean) =>
  isModal ? Beneficiaries : Introduction;

const Wizard: React.FC<WizardProps> = ({
  modal = false,
  setIsModalOpen,
  overrideStep,
  isUserEnrolled,
  onCancel,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentStep } = useSelector((state: RootState) => state.wizard);
  const { enrolledStatus, id: userId } = useSelector(
    (state: RootState) => state.user,
  );
  const { saveProgress, clearProgress, getStartingStep } = useWizardProgress();

  // Track wizard step timing
  const { trackStepCompletion } = useWizardStepTimer(
    currentStep,
    userId,
    modal,
  );

  const navigationHandlers = {
    handleNext: () => {
      trackStepCompletion(); // Track time spent on current step
      saveProgress(currentStep);
      dispatch(incrementCurrentStep());
    },
    handlePrevious: () => {
      trackStepCompletion(); // Track time spent on current step
      dispatch(decrementCurrentStep());
    },
    isUserEnrolled: isUserEnrolled,
    onCancel: onCancel,
  };

  useEffect(() => {
    if (!isUserEnrolled) {
      if (isEnrolledStatus(enrolledStatus)) {
        clearProgress();
        navigate("/");
        return;
      }
    }

    if (!overrideStep) {
      const startingStep = getStartingStep(modal);
      if (currentStep !== startingStep) {
        dispatch(setCurrentStep(startingStep));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderWizardContent = () => {
    const steps = modal ? WIZARD_STEPS.MODAL : WIZARD_STEPS.NON_MODAL;

    const stepToUse = overrideStep || currentStep;
    const Component =
      steps[stepToUse as keyof typeof steps] || getDefaultComponent(modal);

    return <Component {...navigationHandlers} />;
  };

  if (!modal) {
    return (
      <div>
        <ContentWrapper>
          <header className="bg-white font-outfit p-[16px]">
            <h1 className="w-full lg:w-[644px] m-auto font-medium text-[18px]">
              Set Up Your Sootchy Account
            </h1>
          </header>
          <main
            //className="flex w-full h-full justify-center items-center bg-gradient-to-b from-custom-blue to-custom-purple py-10 px-[16px]"
            className="flex w-full h-full justify-center items-center py-10 px-[16px]"
          >
            {renderWizardContent()}
          </main>
        </ContentWrapper>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[999998]">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => setIsModalOpen?.(false)}
        role="presentation"
      />
      <div className="relative bg-white rounded-[12px] shadow-xl max-h-[90vh] overflow-y-auto">
        <main
          className={`${
            isUserEnrolled && "bg-white"
          } flex flex-col w-full h-full `}
        >
          <div className="px-[24px] border-b-[1px] border-[#E6E6E6]">
            <div className="py-[16px] flex justify-between items-center">
              <h2 className="text-[18px] font-medium">Add Beneficiary</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsModalOpen?.(false)}
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="px-[24px] py-[24px]">{renderWizardContent()}</div>
        </main>
      </div>
    </div>
  );
};

export default Wizard;
